import Link from 'next/link';
import React, { FC, MouseEvent, useContext, useState } from 'react';
import calculateLineItemQuantity from '../../../helpers/calculate-line-item-quantity';
import interpolateContent from '../../../helpers/interpolate-content';
import useDidMountEffect from '../../../hooks/use-after-mount-effect/use-after-mount-effect';
import useIsBreakpoint from '../../../hooks/use-is-breakpoint/use-is-breakpoint';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import BasketFeatureType from '../../../models/basket-features-type';
import { BasketTableLabelsType } from '../../../models/basket-table-labels-type';
import BreakpointEnum from '../../../models/breakpoint-enum';
import { CheckoutBasketTableLabelsType } from '../../../models/checkout-basket-table-labels-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import ProductBasketLineItemMarkerEnum from '../../../models/product-basket-line-item-marker-enum';
import { ProductBasketLineItemQuantityBreakType } from '../../../models/product-basket-line-item-quantity-break-type';
import { ProductBasketLineItemType } from '../../../models/product-basket-line-item-type';
import { ProductLineItemType } from '../../../models/product-line-item-type';
import ProductTypeEnum from '../../../models/product-type-enum';
import FieldCheckbox from '../../atoms/field-checkbox/field-checkbox';
import FieldText from '../../atoms/field-text/field-text';
import IcomoonIconButton from '../../atoms/icomoon-icon-button/icomoon-icon-button';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import ProductImage from '../../atoms/product-image/product-image';
import ProductOverview from '../../atoms/product-overview/product-overview';
import TableData from '../../atoms/table-data/table-data';
import TableRow from '../../atoms/table-row/table-row';
import TextLink from '../../atoms/text-link/text-link';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import RichText from '../rich-text/rich-text';
import Tooltip from '../tooltip/tooltip';
import close from '../../providers/overlay/close.overlay.actions';

interface Props {
  checkoutLabels?: CheckoutBasketTableLabelsType;
  confirmedBackOrder: boolean;
  features: BasketFeatureType;
  highlighted?: boolean;
  labels: BasketTableLabelsType;
  lineItem: ProductLineItemType;
  onAdd: (quantity: number) => void;
  onChangeQuantity: (quantity: number) => void;
  onCheckLeadTimes: (quantity: number) => void;
  onCheckRecommendedAlternatives: () => void;
  onConfirmedBackOrderToggle: () => void;
  onRemove: () => void;
  onReplace: (quantity: number) => void;
  onUpdateQuantity: (quantity: number) => void;
}

const BasketTableRow: FC<Props> = ({
  checkoutLabels,
  confirmedBackOrder,
  features,
  highlighted,
  labels: {
    callOffProductLabel,
    discountColumnLabel,
    priceColumnLabel,
    removeButtonLabel,
    removeLinkText,
    quantityColumnLabel,
    stockColumnLabel,
    subTotalColumnLabel,
    updateLinkText,
    updateQuantityLinkLabel,
  },
  lineItem,
  onAdd,
  onChangeQuantity,
  onCheckLeadTimes,
  onCheckRecommendedAlternatives,
  onConfirmedBackOrderToggle,
  onRemove,
  onReplace,
  onUpdateQuantity,
}: Props) => {
  const {
    assemblyProductLabel,
    awaitingStockLabel,
    backOrderAndSendAsSingleDelivery,
    bulkSaveAvailableLabel,
    checkLeadTimesLabel,
    clearanceProductLabel,
    hazardousProductLabel,
    nonCatOrAssemblyProductLabel,
    numberInStockLabel,
    recommendedAlternativesAvailable,
    recommendedAlternativesPopupAddButtonText,
    recommendedAlternativesPopupReplaceButtonText,
    transientProductLabel,
    viewOptions,
  } = checkoutLabels || {};

  const { pushToDataLayer } = useContext<DataLayerContextType>(
    DataLayerContext
  );
  const overlayDispatch = useContext(OverlayDispatchContext);

  const getQuantity = () =>
    ('quantity' in lineItem
      ? lineItem.quantity
      : calculateLineItemQuantity(lineItem as ProductBasketLineItemType)
          .total) || 1;

  const [quantityValue, setQuantityValue] = useState<number>(getQuantity());

  const breakpoint = useIsBreakpoint();

  const quantityBreak = (lineItem as ProductBasketLineItemType).quantityBreaks?.reduce<ProductBasketLineItemQuantityBreakType | null>(
    (previousBreak, currentBreak) =>
      currentBreak.quantity > quantityValue &&
      (!previousBreak || currentBreak.quantity < previousBreak?.quantity)
        ? currentBreak
        : previousBreak,
    null
  );

  let backgroundColorClass = 'bg-grey-50';

  if (highlighted) {
    backgroundColorClass = 'bg-light-blue-dark';
  } else if (
    features.checkoutLayout &&
    quantityValue <= (lineItem as ProductBasketLineItemType).quantityInStock
  ) {
    backgroundColorClass = 'bg-white';
  }

  const showAnyButtons =
    features.addButton || features.replaceButton || features.removeButton;

  const totalColumns =
    4 +
    Number(features.discountColumn) +
    Number(features.inStockColumn) +
    Number(features.subtotalColumn) +
    Number(showAnyButtons);

  const productIsNonCatalogue =
    (lineItem as ProductBasketLineItemType).isCatalogueProduct === false;

  const productIsAssembly =
    (lineItem as ProductBasketLineItemType).assemblySiteFlag === 'F';

  const productIsClearance =
    (lineItem as ProductBasketLineItemType).productMarker ===
    ProductBasketLineItemMarkerEnum.Clearance;

  const productIsHazardous =
    (lineItem as ProductBasketLineItemType).productMarker ===
    ProductBasketLineItemMarkerEnum.Hazardous;

  const productIsTransient =
    (lineItem as ProductBasketLineItemType).transient > 0;

  const productHasCallOffOrder =
    (lineItem as ProductBasketLineItemType).callOffSoNo !== '' &&
    (lineItem as ProductBasketLineItemType).callOffSoNo !== undefined;

  const productHasWarning =
    productIsNonCatalogue ||
    productIsAssembly ||
    productIsClearance ||
    productIsHazardous ||
    productIsTransient ||
    productHasCallOffOrder;

  const hasBottomItems =
    (features.favouritesLayout && breakpoint === BreakpointEnum.lg) ||
    (features.productWarnings && productHasWarning) ||
    (features.quantityBreaksSave && quantityBreak) ||
    (features.recommendedAlternatives &&
      quantityValue >
        (lineItem as ProductBasketLineItemType).quantityInStock) ||
    (features.checkoutLayout &&
      quantityValue > (lineItem as ProductBasketLineItemType).quantityInStock);

  const productAlt = [
    (lineItem as ProductBasketLineItemType).cataloguePageNumber &&
      `Page: ${(lineItem as ProductBasketLineItemType).cataloguePageNumber}`,
    lineItem.product.properties.discountGroupIcon &&
      `Disc Group: ${lineItem.product.properties.discountGroupIcon}`,
  ]
    .filter((part) => !!part)
    .join(', ');

  const getClassNames = (): string => {
    if (features.favouritesLayout) {
      return 'lg:rounded-tr-lg xl:rounded-tr-none';
    }

    if (features.checkoutLayout) {
      return hasBottomItems ? 'lg:rounded-tr-lg' : 'lg:rounded-r-lg';
    }

    return '';
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useDidMountEffect(() => {
    setQuantityValue(getQuantity());
  }, [getQuantity()]);

  const openSingleDeliveryTooltip = (event: MouseEvent): void => {
    overlayDispatch(
      open(
        <Tooltip
          event={event}
          position={breakpoint === BreakpointEnum.md ? 'right' : 'left'}
          size="large"
        >
          <div className="flex flex-col space-y-2">
            {checkoutLabels.receiveAsSingleDeliveryTooltipTitle && (
              <h4 className="page-heading-four">
                {checkoutLabels.receiveAsSingleDeliveryTooltipTitle}
              </h4>
            )}

            {checkoutLabels.receiveAsSingleDeliveryTooltipText && (
              <RichText>
                {checkoutLabels.receiveAsSingleDeliveryTooltipText}
              </RichText>
            )}

            {checkoutLabels.receiveAsSingleDeliveryTooltipLink && (
              <TextLink
                {...checkoutLabels.receiveAsSingleDeliveryTooltipLink}
                showIcon
              />
            )}
          </div>
        </Tooltip>
      )
    );
  };

  return (
    <>
      <TableRow
        className={`${backgroundColorClass} ${
          !hasBottomItems && features.checkoutLayout ? 'pb-1 md:pb-2' : ''
        } ${
          features.checkoutLayout ? 'border lg:border-none border-grey-100' : ''
        } duration-200 items-center transition-colors`}
      >
        <TableData
          className={`${hasBottomItems ? 'rounded-tl-lg' : 'rounded-l-lg'} ${
            features.checkoutLayout
              ? `${
                  hasBottomItems ? '' : 'lg:border-b'
                } lg:border-l lg:border-t border-grey-100`
              : ''
          } w-24 lg:w-auto}`}
        >
          {lineItem.product.url && !productIsNonCatalogue ? (
            <Link href={lineItem.product.url.includes("category-landing") ? lineItem.product.url : `category-landing/${lineItem.product.url}`
            } passHref>
              <a className="bg-white -mr-6 p-2 rounded-lg" href="replace" onClick={() => overlayDispatch(close())}>
                <ProductImage
                  src={lineItem.product.properties.webImageSmall60}
                  title={productAlt}
                  alt={productAlt}
                />
              </a>
            </Link>
          ) : (
            <div className="bg-white -mr-6 p-2 rounded-lg">
              <ProductImage
                src={lineItem.product.properties.webImageSmall60}
                title={productAlt}
                alt={productAlt}
              />
            </div>
          )}
        </TableData>

        <TableData
          className={`${
            features.checkoutLayout
              ? `${
                  hasBottomItems ? '' : 'lg:border-b'
                } lg:border-t border-grey-100`
              : ''
          } w-8/12 md:w-10/12 lg:w-auto`}
        >
          <div className="lg:-mr-6">
            <ProductOverview lineItem={lineItem as ProductBasketLineItemType} />
          </div>
        </TableData>

        <td
          className="border-t lg:border-t-0 border-grey-200 mx-6 my-2 table-cell lg:hidden"
          style={{ width: 'calc(100% - 3rem)' }}
        />

        {features.inStockColumn && (
          <>
            <TableData
              className={`${
                features.checkoutLayout
                  ? `${
                      hasBottomItems ? '' : 'lg:border-b'
                    } lg:border-t border-grey-100`
                  : ''
              }  self-start w-12/12 md:w-4/12 lg:w-auto`}
              label={stockColumnLabel}
            >
              <div className="flex items-center space-x-2 lg:-mr-6">
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    (lineItem as ProductBasketLineItemType).quantityInStock <
                    quantityValue
                      ? 'bg-invalid-dark'
                      : 'bg-french-blue'
                  }`}
                />

                <div>
                  <span className="text-sm">
                    {(lineItem as ProductBasketLineItemType).quantityInStock ===
                    0
                      ? awaitingStockLabel
                      : interpolateContent(
                          numberInStockLabel || '{0} In Stock',
                          (lineItem as ProductBasketLineItemType)
                            .quantityInStock
                        )}
                  </span>

                  {(lineItem as ProductBasketLineItemType).quantityInStock <
                    quantityValue && (
                    <TextLink
                      name={checkLeadTimesLabel}
                      textSize="sm"
                      onClick={() => onCheckLeadTimes(quantityValue)}
                    />
                  )}
                </div>
              </div>
            </TableData>

            <td
              className="border-t md:border-t-0 border-grey-200 mx-6 my-2 table-cell md:hidden"
              style={{ width: 'calc(100% - 3rem)' }}
            />
          </>
        )}

        {features.discountColumn && (
          <TableData
            className={`${features.inStockColumn ? 'md:w-4/12' : 'md:w-6/12'} ${
              features.checkoutLayout
                ? `${
                    hasBottomItems ? '' : 'lg:border-b'
                  } lg:border-t border-grey-100`
                : ''
            } ${
              features.inStockColumn
                ? 'text-left md:text-center lg:text-left'
                : ''
            } self-start w-6/12 lg:w-auto`}
            label={discountColumnLabel}
          >
            <span className="text-sm">{lineItem.discount}%</span>
          </TableData>
        )}

        <TableData
          className={`${features.inStockColumn ? 'md:w-4/12' : 'md:w-6/12'} ${
            features.checkoutLayout
              ? `${
                  hasBottomItems ? '' : 'lg:border-b'
                } lg:border-t border-grey-100`
              : ''
          } text-right lg:text-left w-6/12 lg:w-auto`}
          label={priceColumnLabel}
        >
          <div>
            {lineItem.listPriceFormatted &&
              lineItem.listPrice > lineItem.buyPrice && (
                <span className="text-sm block line-through text-grey-600">
                  {lineItem.listPriceFormatted}
                </span>
              )}

            <span className="text-sm block">{lineItem.buyPriceFormatted}</span>
          </div>
        </TableData>

        <td className="border-t lg:border-t-0 border-grey-200 mx-6 my-2 table-cell lg:hidden w-full" />

        <TableData
          className={`${
            features.checkoutLayout
              ? `${
                  hasBottomItems ? '' : 'lg:border-b'
                } lg:border-t border-grey-100`
              : ''
          } w-6/12 lg:w-auto`}
          label={quantityColumnLabel}
        >
          {features.quantityReadonly && <span>{quantityValue}</span>}

          {!features.quantityReadonly && (
            <div
              className={`-mr-6 ${
                !features.subtotalColumn && 'xl:mr-0'
              } mt-1 lg:mt-0 w-32 lg:w-auto flex space-x-2 lg:space-x-0 lg:flex-col`}
            >
              <FieldText
                alwaysShowRing
                backgroundColor="bg-white"
                onChange={(value: string) => {
                  const numericValue = parseInt(value, 10);
                  onChangeQuantity(numericValue);
                  setQuantityValue(numericValue);
                }}
                min={1}
                max={
                  productIsClearance
                    ? (lineItem as ProductBasketLineItemType).quantityInStock
                    : null
                }
                paddingX="half"
                step={1}
                textAlign="center"
                type="number"
                value={quantityValue}
              />
              {features.updateQuantityButton && (
                <button
                  className={`
                    ${
                      quantityValue < 1 ||
                      (productIsClearance &&
                        parseInt(quantityValue.toString(), 10) >
                          (lineItem as ProductBasketLineItemType)
                            .quantityInStock)
                        ? 'cursor-default text-grey-600'
                        : 'text-french-blue'
                    }
                    lg:mt-1 focus:outline-none w-full text-sm
                  `}
                  disabled={
                    quantityValue < 1 ||
                    (productIsClearance &&
                      parseInt(quantityValue.toString(), 10) >
                        (lineItem as ProductBasketLineItemType).quantityInStock)
                  }
                  onClick={() => onUpdateQuantity(quantityValue)}
                  type="button"
                >
                  {updateLinkText || updateQuantityLinkLabel}
                </button>
              )}
            </div>
          )}
        </TableData>

        {features.subtotalColumn && (
          <TableData
            className={`${getClassNames()} ${
              features.checkoutLayout
                ? `${
                    hasBottomItems ? '' : 'lg:border-b'
                  } lg:border-r lg:border-t border-grey-100`
                : ''
            } text-right lg:text-left w-6/12 lg:w-auto`}
            label={subTotalColumnLabel}
          >
            <span className="text-sm font-semibold">
              {lineItem.lineValueFormatted}
            </span>
          </TableData>
        )}

        {showAnyButtons &&
          !(features.favouritesLayout && breakpoint === BreakpointEnum.lg) && (
            <td
              className={`${
                hasBottomItems ? 'rounded-tr-lg' : 'lg:rounded-r-lg'
              } border-t lg:border-t-0 lg:border-l border-grey-200 mt-1 mx-6 lg:mt-0 lg:mx-0 py-3 w-full`}
              style={{ width: 'calc(100% - 3rem)' }}
            >
              <div className="flex justify-around">
                {features.removeButton && (
                  <div className="flex lg:flex-col items-center justify-center">
                    <IcomoonIconButton
                      className="-mb-2px"
                      color="french-blue"
                      name="trash"
                      onClick={() => onRemove()}
                    />

                    <span className="text-sm">
                      {removeLinkText || removeButtonLabel}
                    </span>
                  </div>
                )}

                {features.replaceButton && (
                  <div className="flex lg:flex-col items-center justify-center">
                    <IcomoonIconButton
                      className="-mb-2px"
                      color="french-blue"
                      name="replace"
                      onClick={() => onReplace(quantityValue)}
                    />

                    <span className="text-sm">
                      {recommendedAlternativesPopupReplaceButtonText}
                    </span>
                  </div>
                )}

                {features.addButton && (
                  <div className="flex lg:flex-col items-center justify-center">
                    <IcomoonIconButton
                      className="-mb-2px"
                      color="french-blue"
                      name="add"
                      onClick={() => onAdd(quantityValue)}
                    />

                    <span className="text-sm">
                      {recommendedAlternativesPopupAddButtonText}
                    </span>
                  </div>
                )}
              </div>
            </td>
          )}

        {features.checkoutLayout &&
          (breakpoint === BreakpointEnum.sm ||
            breakpoint === BreakpointEnum.md) &&
          quantityValue >
            (lineItem as ProductBasketLineItemType).quantityInStock && (
            <>
              <td className="border-t lg:border-t-0 border-grey-200 mx-6 my-2 table-cell lg:hidden w-full" />

              <td className="my-2 rounded-b-lg w-full" colSpan={totalColumns}>
                <div className="flex items-center mx-6 pb-2">
                  <FieldCheckbox
                    label={
                      <div className="flex flex-wrap">
                        <span>
                          {checkoutLabels.receiveAsSingleDeliveryLabel}&nbsp;
                        </span>

                        <TextLink
                          className="inline"
                          name={
                            checkoutLabels.receiveAsSingleDeliveryTooltipLabel
                          }
                          onClick={(event: MouseEvent) => {
                            event.stopPropagation();
                            event.preventDefault();
                            openSingleDeliveryTooltip(event);
                          }}
                        />
                      </div>
                    }
                    value={confirmedBackOrder}
                    onChange={() => onConfirmedBackOrderToggle()}
                  />
                </div>
              </td>
            </>
          )}
      </TableRow>

      {features.favouritesLayout && breakpoint === BreakpointEnum.lg && (
        <tr className="bg-grey-50 table-row w-full">
          <td className="rounded-b-lg w-full" colSpan={totalColumns - 1}>
            <div className="border-t border-grey-200 flex items-center justify-center mx-6 py-2">
              <IcomoonIconButton
                className="-mb-2px"
                color="french-blue"
                name="trash"
                onClick={() => onRemove()}
              />
              <span className="text-sm">
                {removeLinkText || removeButtonLabel}
              </span>
            </div>
          </td>
        </tr>
      )}

      {features.checkoutLayout &&
        (breakpoint === BreakpointEnum.lg ||
          breakpoint === BreakpointEnum.xl ||
          breakpoint === BreakpointEnum.xxl) &&
        quantityValue >
          (lineItem as ProductBasketLineItemType).quantityInStock && (
          <tr
            className={`${backgroundColorClass} duration-200 transition-colors`}
          >
            <td
              className="border-b border-l border-r border-grey-100 rounded-b-lg w-full"
              colSpan={totalColumns}
            >
              <div className="flex items-center justify-end mx-6 pb-2">
                <FieldCheckbox
                  label={
                    <div className="flex flex-wrap">
                      <span>
                        {checkoutLabels.receiveAsSingleDeliveryLabel}&nbsp;
                      </span>

                      <TextLink
                        className="inline"
                        name={
                          checkoutLabels.receiveAsSingleDeliveryTooltipLabel
                        }
                        onClick={(event: MouseEvent) => {
                          event.stopPropagation();
                          event.preventDefault();
                          openSingleDeliveryTooltip(event);
                        }}
                      />
                    </div>
                  }
                  value={confirmedBackOrder}
                  onChange={() => onConfirmedBackOrderToggle()}
                />
              </div>
            </td>
          </tr>
        )}

      {features.productWarnings && productHasWarning && (
        <tr
          className={`${backgroundColorClass} duration-200 transition-colors`}
        >
          <td
            colSpan={totalColumns}
            className={`${
              (features.quantityBreaksSave && quantityBreak) ||
              (features.recommendedAlternatives &&
                quantityValue >
                  (lineItem as ProductBasketLineItemType).quantityInStock)
                ? ''
                : 'rounded-b-lg'
            } flex lg:table-cell justify-center`}
          >
            {productIsNonCatalogue && (
              <div className="flex items-center space-x-2 bg-floral my-2 mx-4 p-2 rounded-lg text-grey-600 text-sm">
                <IcomoonIcon color="french-blue" name="info-circle" size="lg" />
                <span>{nonCatOrAssemblyProductLabel}</span>
              </div>
            )}
            {productIsAssembly && (
              <div className="flex items-center space-x-2 bg-floral my-2 mx-4 p-2 rounded-lg text-grey-600 text-sm">
                <IcomoonIcon color="french-blue" name="info-circle" size="lg" />
                <span>{assemblyProductLabel}</span>
              </div>
            )}
            {productIsTransient && (
              <div className="flex items-center space-x-2 bg-floral my-2 mx-4 p-2 rounded-lg text-grey-600 text-sm">
                <IcomoonIcon color="french-blue" name="info-circle" size="lg" />
                <span>{transientProductLabel}</span>
              </div>
            )}
            {productIsHazardous && (
              <div className="flex items-center space-x-2 bg-floral my-2 mx-4 p-2 rounded-lg text-grey-600 text-sm">
                <IcomoonIcon color="french-blue" name="info-circle" size="lg" />
                <span>{hazardousProductLabel}</span>
              </div>
            )}
            {productIsClearance && (
              <div className="flex items-center space-x-2 bg-floral my-2 mx-4 p-2 rounded-lg text-grey-600 text-sm">
                <IcomoonIcon color="french-blue" name="info-circle" size="lg" />
                <span>{clearanceProductLabel}</span>
              </div>
            )}
            {productHasCallOffOrder && (
              <div className="flex items-center space-x-2 bg-floral my-2 mx-4 p-2 rounded-lg text-grey-600 text-sm">
                <IcomoonIcon color="french-blue" name="info-circle" size="lg" />
                <span>
                  {callOffProductLabel.replace('{0}', lineItem.callOffSoNo)}
                </span>
              </div>
            )}
          </td>
        </tr>
      )}

      {features.quantityBreaksSave && quantityBreak && (
        <tr
          className={`${backgroundColorClass} duration-200 transition-colors`}
        >
          <td
            className={`${
              features.recommendedAlternatives &&
              quantityValue >
                (lineItem as ProductBasketLineItemType).quantityInStock
                ? ''
                : 'rounded-b-lg'
            } flex lg:table-cell justify-center`}
            colSpan={totalColumns}
          >
            <div className="flex items-center space-x-2 bg-turquoise my-2 mx-4 p-2 rounded-lg text-white text-sm">
              <IcomoonIcon color="white" name="info-circle" size="lg" />
              <span>
                {interpolateContent(
                  bulkSaveAvailableLabel,
                  quantityBreak.quantity,
                  quantityBreak.savingPerUnitFormatted,
                  quantityBreak.totalSavingFormatted
                )}
              </span>
            </div>
          </td>
        </tr>
      )}
      {features.recommendedAlternatives &&
        quantityValue >
          (lineItem as ProductBasketLineItemType).quantityInStock &&
        !productIsClearance && (
          <tr
            className={`${backgroundColorClass} duration-200 transition-colors`}
          >
            <td
              className="flex lg:table-cell justify-center border-t border-grey-200 rounded-b-lg"
              colSpan={totalColumns}
            >
              <div className="flex flex-col lg:flex-row items-center justify-between space-y-2 lg:space-y-0 my-2 mx-6 text-sm">
                <div className="flex flex-col md:flex-row items-center space-x-2">
                  {(lineItem as ProductBasketLineItemType)
                    .reccomendedAlternative && (
                    <>
                      <IcomoonIcon
                        color="french-blue"
                        name="info-circle"
                        size="lg"
                      />
                      <span className="text-sm">
                        {recommendedAlternativesAvailable} -
                      </span>
                      <TextLink
                        textSize="sm"
                        name={viewOptions}
                        onClick={() => onCheckRecommendedAlternatives()}
                      />
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <FieldCheckbox
                    label={backOrderAndSendAsSingleDelivery}
                    labelSize="sm"
                    onChange={() => {
                      onConfirmedBackOrderToggle();
                      pushToDataLayer(GtmEventNameEnum.BasketSingle, {
                        productType: ProductTypeEnum.Website,
                      });
                    }}
                    value={confirmedBackOrder}
                  />
                </div>
              </div>
            </td>
          </tr>
        )}

      <tr className="h-3" />
    </>
  );
};

BasketTableRow.defaultProps = {
  checkoutLabels: null,
  highlighted: false,
};

export default BasketTableRow;
